/**
 * Pure, read-only (non-setState) data/filter/sort helper functions used by
 * EvidenceTable.tsx. Extracted verbatim (no logic changes) purely to
 * reduce the size of the main component file. Each function receives
 * explicit parameters for every value it needs instead of relying on
 * `this`, so behavior is identical to the original inline class methods.
 */
import {
  BasicUserInfo,
  ColorCriteria,
  EvidenceColumn,
  EvidenceColumnGroup,
  EvidencePeriodData,
  EvidenceType,
  Measurement,
  MeasurementType,
  NeedsAreaTag,
  PossibleValue,
  Student,
  StudentClass,
  StudentEntry,
  StudentRow,
} from "../../../../../../store/onboarding/types";
import { Grade } from "../../../../../../store/groups/types";
import { LocalStorageKeys } from "../../../../../../utils/LocalStorageUtils";
import { getFullName } from "../../../../../../utils/NamesUtils";
import { DisplayedColumn, ColumnNames, gradeLevels } from "./Types";
import { calculateRangeFromCriteria } from "./Helpers";
import { InterventionGroup } from "../../../../../../store/onboarding/cases/types";

// Extracted verbatim from EvidenceTable.demographicsFilter
export const demographicsFilter = (
  studentRows: StudentRow[] = [],
  specialEducationFilter: boolean | undefined,
  ellFilter: boolean | undefined,
  sldFilter: boolean | undefined,
  freeOrReducedLunchFilter: boolean | undefined
) => {
  if(specialEducationFilter != undefined) {
    studentRows =  studentRows
    .filter((studentRow) =>  studentRow.student.special_education == specialEducationFilter)
  }
  if(ellFilter != undefined) {
    studentRows =  studentRows
    .filter((studentRow) =>  studentRow.student.ell == ellFilter)
  }
  if(sldFilter != undefined) {
    studentRows =  studentRows
    .filter((studentRow) =>  studentRow.student.sld == sldFilter)
  } 
  if(freeOrReducedLunchFilter != undefined) {
    studentRows =  studentRows
    .filter((studentRow) =>  studentRow.student.free_or_reduced_lunch == freeOrReducedLunchFilter)
  } 
 // console.log(studentRows.length)
  return studentRows;
};

// Extracted verbatim from EvidenceTable.rowsFilter
// export const rowsFilter = (
//   studentRow: StudentRow,
//   props: {
//     interventionGroup?: any;
//     schoolIdsFilter?: number[];
//     teacherIdsFilter?: number[];
//     classFilter?: number[];
//     gradeLevelFilter?: Grade[];
//     selectedClass?: any;
//     evidencePeriodData?: EvidencePeriodData;
//     studentsFilter?: string;
//     teacherIdFilter?: number[];
//     raceFilter?: string[];
//     raceOtherFilter?: string[];
//     genderFilter?: string[];
//     raceInfo?: { races: string[] };
//   }
// ) => {
//   const {
//     interventionGroup,
//     schoolIdsFilter,
//     teacherIdsFilter,
//     classFilter,
//     gradeLevelFilter,
//     selectedClass,
//     evidencePeriodData,
//     studentsFilter,
//     teacherIdFilter,
//     raceFilter,
//     raceOtherFilter,
//     genderFilter
//   } = props;
//   let result: boolean = true;

//   if (interventionGroup) {
//     return true;
//   }

//   if (teacherIdFilter?.length) {
//     result =
//       result &&
//       !!studentRow.student.teachers?.some(
//         (teacher: BasicUserInfo) => teacherIdFilter.includes(teacher.id!)
//       );
//   }

//   if (studentsFilter) {
//     result =
//       result &&
//       getFullName(studentRow.student)
//         .toLowerCase()
//         .includes(studentsFilter.toLowerCase());
//   }

//   if (schoolIdsFilter && schoolIdsFilter.length > 0) {
//     result =
//       result &&
//       studentRow.student
//         .teachers!.flatMap((teacher: BasicUserInfo) => teacher.schools || [])
//         .some((schoolId: number) => schoolId && schoolIdsFilter.includes(schoolId));
//   }

//   if (teacherIdsFilter && teacherIdsFilter.length > 0) {
//     result =
//       result &&
//       studentRow.student.teachers!.some(
//         (teacher: BasicUserInfo) => teacher.id && teacherIdsFilter.includes(teacher.id)
//       );
//   }
//   if (classFilter?.length && selectedClass) {
//     result =
//       result &&
//       studentRow.student.teachers!.some((st: BasicUserInfo) => st.classes?.find((cls: StudentClass) => classFilter.includes(cls.id)));
//   }
//   if (evidencePeriodData && gradeLevelFilter && gradeLevelFilter.length > 0) {
//     let resultsFromStudentEntries = evidencePeriodData.evidence_column_groups.some((ecg: EvidenceColumnGroup) =>
//       ecg.evidence_columns.some((ec: EvidenceColumn) =>
//         ec.student_entries.some(
//           (se: StudentEntry) =>
//             se?.student_id === studentRow?.student.id &&
//             se?.grade &&
//             gradeLevelFilter.includes(se.grade)
//         )
//       )
//     );
//     let resultsFromStudentRows = evidencePeriodData.student_rows.some(
//       (sr: StudentRow) =>
//         sr?.student.id === studentRow?.student.id &&
//         sr?.grade &&
//         gradeLevelFilter.includes(sr.grade)
//     );
//     result = result && (resultsFromStudentEntries || resultsFromStudentRows)
//   }
//   if(raceFilter?.length) {
//     let filteredRaces = props.raceInfo?.races
//     .map((race, index) =>({
//       value: index,
//       label: race
//     })).filter((race) => raceFilter.includes(race.label));
//     result = result && filteredRaces?.some((races) => races.value == studentRow?.student?.race)! || false;
//   }
//   //console.log(studentRow?.student?.race_other_value!, raceOtherFilter)
//   if(raceOtherFilter?.length) {
//     result =
//       result &&
//       raceOtherFilter.includes(studentRow?.student?.race_other_value!);
//   }
//   if(genderFilter?.length) {
//     result =
//       result &&
//       genderFilter.includes(studentRow?.student?.gender!);
//   }
//   return result;
// };

// Extracted verbatim from EvidenceTable.teachersFilter
export const teachersFilter = (studentRow: StudentRow, teacherIdFilter?: number[]) => {
  if (!teacherIdFilter) return true;
  return studentRow.student.teachers!.some(
    (teacher: BasicUserInfo) => teacherIdFilter.includes(teacher.id!)
  );
};

// Extracted verbatim from EvidenceTable.interventionGroupFilter
export const interventionGroupFilter = (studentRow: StudentRow, interventionGroup?: InterventionGroup) => {
  if (!interventionGroup) return true;
  return interventionGroup.students.some(
    (student: Student) => student.id === studentRow.student.id
  );
};

// Extracted verbatim from EvidenceTable.getDisplayedColumnsFromStorage
export const getDisplayedColumnsFromStorage = (
  evidencePeriodData: EvidencePeriodData,
  stateDisplayedColumns: Array<DisplayedColumn>
) => {
    const displayedColumnsData = localStorage.getItem(
      LocalStorageKeys.DisplayedColumns
    );
    const storageColumns = displayedColumnsData
      ? JSON.parse(displayedColumnsData)
      : [];
    const result = [...evidencePeriodData.evidence_column_groups.map(
      (evidenceColumnGroup) => {

        const existingDisplayedColumn = stateDisplayedColumns.find(
          (displayedColumn: DisplayedColumn) =>
            displayedColumn.evidenceColumnGroupId === evidenceColumnGroup.id
        );
        if (existingDisplayedColumn) {
          return existingDisplayedColumn;
        } else {
          const existingStorageColumn = storageColumns.find(
            (displayedColumn: DisplayedColumn) =>
              displayedColumn.evidenceColumnGroupId === evidenceColumnGroup.id
          );
          if (existingStorageColumn) {
            return existingStorageColumn;
          } 
          else {
            const displayedColumn = {
              evidenceColumnGroupId: evidenceColumnGroup.id,
              evidenceColumns: [...evidenceColumnGroup.evidence_columns.map(
                (evidenceColumn: EvidenceColumn) => ({
                  evidenceColumnId: evidenceColumn.id,
                  show: true,
                })
              )],
            };
            storageColumns.push(displayedColumn);
            return displayedColumn;
          }
        }
      }
    )];
    localStorage.setItem(
      LocalStorageKeys.DisplayedColumns,
      JSON.stringify(storageColumns)
    );
    return result;
};

// Extracted verbatim from EvidenceTable.getEvidenceValue
export const getEvidenceValue = (
  evidenceColumn: EvidenceColumn,
  studentRow: StudentRow
) => {
  let value: number | undefined;
  const studentEntry = evidenceColumn.student_entries.find(
    (studentEntry: StudentEntry) => studentEntry.student_id === studentRow.student.id
  );
  if (studentEntry) {
    if (evidenceColumn.evidence_type === EvidenceType.Data) {
      if (evidenceColumn.measurement?.type === MeasurementType.CATEGORY) {
        const possibleValue = evidenceColumn.measurement.possible_values!.find(
          (possibleValue: PossibleValue) => possibleValue.order === +studentEntry.value!
        );
        return possibleValue ? possibleValue.order : +studentEntry.value!;
      } else {
        value = +studentEntry.value!;
      }
    }
  }
  return value;
};

// Extracted verbatim from EvidenceTable.filterDisplayedColumns
export const filterDisplayedColumns = (
  evidenceColumnGroupId: number,
  stateDisplayedColumns: Array<DisplayedColumn>
) => (
  evidenceColumn: EvidenceColumn
) => {
  const existingDisplayedColumn = stateDisplayedColumns.find(
    (displayedColumn: DisplayedColumn) =>
      displayedColumn.evidenceColumnGroupId === evidenceColumnGroupId
  );
  if(existingDisplayedColumn) {
    const col = stateDisplayedColumns
      .find(
        (displayedColumn) =>
          displayedColumn.evidenceColumnGroupId === evidenceColumnGroupId
      )!
      .evidenceColumns.find((x) => x.evidenceColumnId === evidenceColumn.id);
    if (col) {
      return col.show;
    } else {
      return true;
    }
  } else {
    return true;
  }
};

// Extracted verbatim from EvidenceTable.getStudentGrade
export const getStudentGrade = (
  evidencePeriodData: EvidencePeriodData | undefined,
  studentId?: number
): Grade | undefined => {
  if (evidencePeriodData && studentId !== undefined) {
    for (const ecg of evidencePeriodData.evidence_column_groups) {
      for (const ec of ecg.evidence_columns) {
        for (const se of ec.student_entries) {
          if (se.student_id === studentId) {
            return se.grade;
          }
        }
      }
    }
  }
  return undefined;
};

// Extracted verbatim from EvidenceTable.sortStudentRowsByName
export const sortStudentRowsByName = (
  columnName: ColumnNames,
  direction: "asc" | "desc",
  evidencePeriodData: EvidencePeriodData | undefined
) => (studentRowA: StudentRow, studentRowB: StudentRow) => {
  const { student: a } = studentRowA;
  const { student: b } = studentRowB;

  if (columnName === ColumnNames.GRADE) {
    const gradeA = getStudentGrade(evidencePeriodData, a.id);
    const gradeB = getStudentGrade(evidencePeriodData, b.id);

    if (!gradeA) return 1;
    if (!gradeB) return -1;

    if (
      gradeLevels.indexOf(gradeA as any) < gradeLevels.indexOf(gradeB as any)
    ) {
      return direction === "asc" ? -1 : 1;
    } else if (
      gradeLevels.indexOf(gradeA as any) > gradeLevels.indexOf(gradeB as any)
    ) {
      return direction === "asc" ? 1 : -1;
    }

    return 0;
  }

  let nameA, nameB;
  if (columnName === ColumnNames.STUDENT_ROW) {
    nameA = studentRowA.id;
    nameB = studentRowB.id;
  } else if (columnName === ColumnNames.STUDENT) {
    nameA = `${a.first_name} ${a.last_name}`.toUpperCase();
    nameB = `${b.first_name} ${b.last_name}`.toUpperCase();
  } else if(columnName == ColumnNames.CLASS) {
    nameA = a
    .teachers!.map(
      (teacher: BasicUserInfo) => (teacher.classes != null) && teacher.classes!.map((classInfo: StudentClass) => `${classInfo.name}`)
    ).filter((classInfo: any) => classInfo != false)
    .join(", ")
    .toUpperCase();
  nameB = b
    .teachers!.map(
      (teacher: BasicUserInfo) => (teacher.classes != null) && teacher.classes!.map((classInfo: StudentClass) => `${classInfo.name}`)
    ).filter((classInfo: any) => classInfo != false)
    .join(", ")
    .toUpperCase();
  } else {
    nameA = a
      .teachers!.map(
        (teacher: BasicUserInfo) => `${teacher.first_name} ${teacher.last_name}`
      )
      .join(", ")
      .toUpperCase();
    nameB = b
      .teachers!.map(
        (teacher: BasicUserInfo) => `${teacher.first_name} ${teacher.last_name}`
      )
      .join(", ")
      .toUpperCase();
  }
  if (nameA < nameB) {
    return direction === "asc" ? -1 : 1;
  }
  if (nameA > nameB) {
    return direction === "asc" ? 1 : -1;
  }
  return 0;
};

// Extracted verbatim from EvidenceTable.sortStudentRowsByDse
export const sortStudentRowsByDse = (
  evidenceColumn: EvidenceColumn,
  direction: "asc" | "desc",
  evidenceGroupType: string,
  evidencePeriodData: EvidencePeriodData | undefined
) => (a: StudentRow, b: StudentRow) => {
  const studentEntryA:
    | StudentEntry
    | undefined = evidenceColumn.student_entries.find(
      (r: StudentEntry) => r.student_id === a.student.id
    );
  const studentEntryB:
    | StudentEntry
    | undefined = evidenceColumn.student_entries.find(
      (r: StudentEntry) => r.student_id === b.student.id
    );

  if (!studentEntryB && !studentEntryA)
    return sortStudentRowsByName(ColumnNames.STUDENT, "asc", evidencePeriodData)(a, b);
  if (!studentEntryB) return -1;
  if (!studentEntryA) return 1;

  if (evidenceColumn.evidence_type === EvidenceType.Data) {
    const valueA = +studentEntryA.value!;
    const valueB = +studentEntryB.value!;
    if (valueA < valueB) {
      return direction === "asc" ? -1 : 1;
    }

    if (valueA > valueB) {
      return direction === "asc" ? 1 : -1;
    }
  }

  if (evidenceColumn.evidence_type === EvidenceType.NeedAreas) {
    let valueA, valueB = null;
    if(evidenceGroupType == 'meta_group_needs') { // Reading area of need
      valueA = (studentEntryA.needs_area_tags != null) && studentEntryA.needs_area_tags.length
        ? studentEntryA.needs_area_tags.map((nat: NeedsAreaTag) => nat.display_name).join(" ").replace(" ", "")
        : null;
      
      valueB = (studentEntryB.needs_area_tags != null) && studentEntryB.needs_area_tags.length
        ? studentEntryB.needs_area_tags.map((nat: NeedsAreaTag) => nat.display_name).join(" ").replace(" ", "")
        : null;

    } else { // behavior area of need
      valueA = (studentEntryA.needs_area_tags != null) && studentEntryA.needs_area_tags.length
      ? studentEntryA.needs_area_tags.sort((a: NeedsAreaTag,b: NeedsAreaTag) => {
          if(a.display_name > b.display_name) {
            return direction === "asc" ? 1 : -1;
          }
          return 0;
      }).length
      : null;
    
    valueB = (studentEntryB.needs_area_tags != null) && studentEntryB.needs_area_tags.length
      ? studentEntryB.needs_area_tags.sort((a: NeedsAreaTag,b: NeedsAreaTag) => {
          if(a.display_name < b.display_name) {
            return direction === "asc" ? -1 : 1;
          }
          return 0;
      }).length
      : null;
    }

    if (!valueA) return 1;
    if (!valueB) return -1;
    if (valueA > valueB) {
      return direction === "asc" ? 1 : -1;
    }
    if (valueA < valueB) {
      return direction === "asc" ? -1 : 1;
    }
    
  }

  if (evidenceColumn.evidence_type === EvidenceType.DiagnosticInfo) {
    const valueA = (studentEntryA.diagnostic_tags != null) && studentEntryA.diagnostic_tags.length
      ? studentEntryA.diagnostic_tags[0].display_name
      : null;
    const valueB = (studentEntryB.diagnostic_tags != null) && studentEntryB.diagnostic_tags.length
      ? studentEntryB.diagnostic_tags[0].display_name
      : null;
    if (!valueA) return 1;
    if (!valueB) return -1;
    if (valueA > valueB) {
      return direction === "asc" ? 1 : -1;
    }
    if (valueA < valueB) {
      return direction === "asc" ? -1 : 1;
    }
  }

  if (evidenceColumn.evidence_type === EvidenceType.DiagnosticFollowUp) {
    if (!studentEntryA?.value) return 1;
    if (!studentEntryB?.value) return -1;
    if (studentEntryA?.value > studentEntryB.value) {
      return direction === "asc" ? 1 : -1;
    }
    if (studentEntryA?.value < studentEntryB.value) {
      return direction === "asc" ? -1 : 1;
    }
  }

  return 0;
};

// Extracted verbatim from EvidenceTable.findDataStudentEntry
export const findDataStudentEntry = (
  evidencePeriodData: EvidencePeriodData | undefined,
  evidenceColumnGroupId: number,
  evidenceColumnId: number,
  studentId: number
) => {
  let dataStudentEntry: StudentEntry | undefined;

  const curEvidenceColumnGroup:
    | EvidenceColumnGroup
    | undefined = evidencePeriodData!.evidence_column_groups.find(
      (evidenceColumnGroup: EvidenceColumnGroup) => evidenceColumnGroup.id === evidenceColumnGroupId
    );
  if (curEvidenceColumnGroup) {
    const curEvidenceColumn:
      | EvidenceColumn
      | undefined = curEvidenceColumnGroup.evidence_columns.find(
        (evidenceColumn: EvidenceColumn) => evidenceColumn.id === evidenceColumnId
      );
    if (curEvidenceColumn) {
      const studentEntry:
        | StudentEntry
        | undefined = curEvidenceColumn.student_entries.find(
          (studentEntry: StudentEntry) => studentEntry.student_id === studentId
        );
      if (studentEntry) {
        if (studentEntry) {
          dataStudentEntry = studentEntry;
        }
      }
    }
  }
  return dataStudentEntry;
};

// Extracted verbatim from EvidenceTable.findEvidenceType
export const findEvidenceType = (
  evidencePeriodData: EvidencePeriodData | undefined,
  evidenceColumnGroupId: number,
  evidenceColumnId: number,
) => {
  let evidenceType: EvidenceType | undefined;

  const curEvidenceColumnGroup:
    | EvidenceColumnGroup
    | undefined = evidencePeriodData!.evidence_column_groups.find(
      (evidenceColumnGroup: EvidenceColumnGroup) => evidenceColumnGroup.id === evidenceColumnGroupId
    );
  if (curEvidenceColumnGroup) {
    const curEvidenceColumn:
      | EvidenceColumn
      | undefined = curEvidenceColumnGroup.evidence_columns.find(
        (evidenceColumn: EvidenceColumn) => evidenceColumn.id === evidenceColumnId
      );
    if (curEvidenceColumn) {
      evidenceType = curEvidenceColumn.evidence_type
    }
  }
  return evidenceType;
};

// Extracted verbatim from EvidenceTable.findColorCriteriaRange
export const findColorCriteriaRange = (
  evidencePeriodData: EvidencePeriodData | undefined,
  evidenceColumnGroupId: number,
  evidenceColumnId: number,
  grade?: Grade,
  timePeriod?: string
) => {
  let measurement: Measurement | undefined;
  let useGradeSpecificColorCriteria: boolean | undefined;

  const curEvidenceColumnGroup:
    | EvidenceColumnGroup
    | undefined = evidencePeriodData!.evidence_column_groups.find(
      (evidenceColumnGroup: EvidenceColumnGroup) => evidenceColumnGroup.id === evidenceColumnGroupId
    );
  
  if (curEvidenceColumnGroup) {
    useGradeSpecificColorCriteria = curEvidenceColumnGroup.assessment.use_grade_specific_color_criteria;
    const curEvidenceColumn:
      | EvidenceColumn
      | undefined = curEvidenceColumnGroup.evidence_columns.find(
        (evidenceColumn: EvidenceColumn) => evidenceColumn.id === evidenceColumnId
      );
    if (curEvidenceColumn) {
      measurement = curEvidenceColumn.measurement;
    }
  }

  let globalRangeMin: number | undefined;
  let globalRangeMax: number | undefined;

  if (useGradeSpecificColorCriteria) {
    // Check custom color criteria filtered by grade and time period
    let filteredCriteria: ColorCriteria[] = [];
    if (measurement?.custom_color_criteria?.length) {
      filteredCriteria = measurement.custom_color_criteria.filter(
        (criteria: ColorCriteria) =>
          criteria.time_period === timePeriod && criteria.grade === grade
      );
    }
    if (filteredCriteria.length) {
      const { rangeMin, rangeMax } = calculateRangeFromCriteria(filteredCriteria);
      globalRangeMin = rangeMin;
      globalRangeMax = rangeMax;
    } 
    // Check grade_specific_color_criteria
    else if (
      measurement?.grade_specific_color_criteria &&
      Object.keys(measurement.grade_specific_color_criteria || {}).length
    ) {
      const gradeData = measurement.grade_specific_color_criteria[
        grade as keyof typeof measurement.grade_specific_color_criteria
      ];
      if (gradeData) {
        const timePeriodData = gradeData[timePeriod as keyof typeof gradeData];
        if (Array.isArray(timePeriodData)) {
          const { rangeMin, rangeMax } = calculateRangeFromCriteria(timePeriodData);
          globalRangeMin = rangeMin;
          globalRangeMax = rangeMax;
        }
      }
    }

  } else {
    // Non-grade-specific criteria
    if (measurement?.custom_color_criteria) {
      const { rangeMin, rangeMax } = calculateRangeFromCriteria(
        measurement.custom_color_criteria
      );
      globalRangeMin = rangeMin;
      globalRangeMax = rangeMax;
    }
  }

  return {
    globalRangeMin,
    globalRangeMax,
  };
};

// Extracted verbatim from EvidenceTable.getColumnWidth
export const getColumnWidth = (
  evidenceColumnGroup: EvidenceColumnGroup,
  evidenceColumn: EvidenceColumn,
  isCompressedViewOfDataTable: boolean | undefined,
  minimal: boolean | undefined,
  stateDisplayedColumns: Array<DisplayedColumn>
) => {
  const coeff =
    window.innerWidth <= 1200 ||
      isCompressedViewOfDataTable ||
      minimal
      ? 0.85
      : 1;
  if (evidenceColumn.evidence_type === EvidenceType.DiagnosticFollowUp)
    return coeff * 160;
  if (evidenceColumn.evidence_type === EvidenceType.DiagnosticInfo)
    return coeff * 570;
  if (evidenceColumn.evidence_type === EvidenceType.BehaviorScreener)
    return coeff * 150;
  if (evidenceColumn.evidence_type === EvidenceType.NeedAreas) return 300;
  if (evidenceColumn.evidence_type === EvidenceType.ExistingBehavior)
    return coeff * 300;
  return evidenceColumnGroup.evidence_columns.filter(
    filterDisplayedColumns(evidenceColumnGroup.id, stateDisplayedColumns)
  ).length === 1
    ? coeff * 240
    : coeff * 110;
};

// Extracted verbatim from EvidenceTable.getNumberDisplayingColumns
export const getNumberDisplayingColumns = (
  evidenceColumnGroupId: number,
  stateDisplayedColumns: Array<DisplayedColumn>
): number => {
  let displayColumn =  stateDisplayedColumns
  .find((dc) => dc.evidenceColumnGroupId === evidenceColumnGroupId);
  if(displayColumn != undefined) {
    return stateDisplayedColumns
      .find((dc) => dc.evidenceColumnGroupId === evidenceColumnGroupId)!
      .evidenceColumns.filter((ec) => ec.show).length;
  } else {
    return 0;
  }
};

// Extracted verbatim from EvidenceTable.checkStudentCellDraggable
export const checkStudentCellDraggable = (
  student: Student,
  isPlanning: boolean | undefined,
  newGroupStudents: Array<Student>
) => {
  return (
    isPlanning &&
    !newGroupStudents.some((s) => s.id === student.id)
  );
};
